import { useEffect } from "react";

import { useField, useFormikContext } from "formik";

type SubmitFirstField<T, E = string> = {
  error: E | undefined;
  hasSubmitted: boolean;
  value: T;
  handleBlur: (e?: React.FocusEvent<HTMLElement>) => Promise<void>;
  handleChange: (e: React.ChangeEvent<unknown>) => void;
  setValue: (value: T) => void;
};

const useSubmitFirstField = <T, E = string>(
  name: string,
): SubmitFirstField<T, E> => {
  const [field, meta, helpers] = useField<T>(name);
  const { submitCount, validateField } = useFormikContext();

  const hasSubmitted = submitCount > 0;

  useEffect(() => {
    if (hasSubmitted) validateField(name);
  }, [field.value, hasSubmitted, name, validateField]);

  const handleBlur = async (
    e?: React.FocusEvent<HTMLElement>,
  ): Promise<void> => {
    if (e) {
      field.onBlur(e);
    } else {
      // shouldValidate: false — dropdown selection fires setValue and this blur in the
      // same tick; a validating setTouched would re-run validation with the pre-setValue
      // values snapshot and its stale result would overwrite the correct one.
      helpers.setTouched(true, false);
    }
    if (hasSubmitted) await validateField(name);
  };

  return {
    error: hasSubmitted ? (meta.error as E | undefined) : undefined,
    handleBlur,
    handleChange: field.onChange,
    hasSubmitted,
    setValue: helpers.setValue,
    value: field.value,
  };
};

export { useSubmitFirstField, type SubmitFirstField };
